import { compressImageFile } from "@/lib/image-compress";
import type { ImageRefInput } from "@/lib/storage/url";

const MAX_IMAGE_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ALLOWED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/svg+xml",
  "image/bmp",
];

const MIME_EXTENSION_MAP: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/heic": "jpg",
  "image/heif": "jpg",
  "image/svg+xml": "svg",
  "image/bmp": "bmp",
};

export type ImageValidationResult = {
  valid: boolean;
  error?: string;
};

export function validateImageFile(file: File): ImageValidationResult {
  if (file.size > MAX_IMAGE_FILE_SIZE) {
    return {
      valid: false,
      error: `File "${file.name}" is too large (max 10MB)`,
    };
  }

  const mimeType = file.type;
  const extension = file.name.toLowerCase();

  const isAllowedMimeType =
    ALLOWED_IMAGE_MIME_TYPES.includes(mimeType) || extension.endsWith(".heic") || extension.endsWith(".heif");

  if (!isAllowedMimeType && !mimeType.startsWith("image/")) {
    return {
      valid: false,
      error: `File "${file.name}" is not a supported image type`,
    };
  }

  return { valid: true };
}

export async function loadImageFilePreviews(files: FileList | File[]): Promise<string[]> {
  const srcs: string[] = [];
  for (const file of Array.from(files)) {
    if (!validateImageFile(file).valid) continue;
    // compress: false — previews only need a displayable URL fast; the
    // upload path re-normalizes (and compresses) the source file anyway.
    const { previewUrl } = await getNormalizedImageFile(file, { compress: false });
    srcs.push(previewUrl);
  }
  return srcs;
}

/**
 * Normalize a user-picked image for upload: HEIC → JPEG, then device-side
 * compression (downscale + webp/jpeg re-encode, see lib/image-compress.ts).
 * Pass { compress: false } for preview-only use where speed matters more
 * than byte size. Fail-open on every step — never blocks the upload.
 */
export async function getNormalizedImageFile(
  file: File,
  opts?: { compress?: boolean }
): Promise<{ file: File; previewUrl: string }> {
  const shouldCompress = opts?.compress !== false;
  const mimeType = file.type;
  const lowerName = file.name.toLowerCase();

  const isHeic =
    mimeType === "image/heic" ||
    mimeType === "image/heif" ||
    lowerName.endsWith(".heic") ||
    lowerName.endsWith(".heif");

  if (isHeic) {
    if (typeof window === "undefined") {
      const previewUrl = URL.createObjectURL(file);
      return { file, previewUrl };
    }

    try {
      const { heicTo } = await import("heic-to");
      const convertedBlob = await heicTo({
        blob: file,
        type: "image/jpeg",
        quality: 0.85,
      });

      const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
      const convertedFile = new File([blob], replaceExtension(file.name, "jpg"), {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      const finalFile = shouldCompress ? await compressImageFile(convertedFile) : convertedFile;
      const previewUrl = URL.createObjectURL(finalFile);

      return { file: finalFile, previewUrl };
    } catch (error) {
      console.error("Error converting HEIC file:", error);
      const previewUrl = URL.createObjectURL(file);
      return { file, previewUrl };
    }
  }

  const finalFile = shouldCompress ? await compressImageFile(file) : file;
  const previewUrl = URL.createObjectURL(finalFile);
  return { file: finalFile, previewUrl };
}

export function getSafeImageExtension(file: File): string {
  const mimeType = file.type;
  if (mimeType && MIME_EXTENSION_MAP[mimeType]) {
    return MIME_EXTENSION_MAP[mimeType];
  }

  const lowerName = file.name.toLowerCase();
  const dotIndex = lowerName.lastIndexOf(".");
  if (dotIndex !== -1) {
    const ext = lowerName.slice(dotIndex + 1);
    if (ext) {
      return ext.replace(/[^a-z0-9]/g, "");
    }
  }

  return "jpg";
}

function replaceExtension(filename: string, newExtension: string): string {
  const base = filename.includes(".") ? filename.slice(0, filename.lastIndexOf(".")) : filename;
  return `${base}.${newExtension}`;
}

/**
 * Run async upload tasks with a bounded worker pool. Results keep task order;
 * a task's own failure handling decides whether its slot is `null` — the pool
 * never throws on a task's behalf. Hoisted from the private copies in
 * card-review-step.tsx / comic-review-step.tsx (2026-08-05, sell-drafts M1).
 */
export async function uploadWithConcurrency<T>(
  tasks: (() => Promise<T | null>)[],
  limit: number,
): Promise<(T | null)[]> {
  const results: (T | null)[] = new Array(tasks.length).fill(null);
  let idx = 0;

  async function worker() {
    while (idx < tasks.length) {
      const i = idx++;
      results[i] = await tasks[i]();
    }
  }

  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

export async function cleanupOrphanedUploads(
  imageUrls: ReadonlyArray<ImageRefInput>,
): Promise<void> {
  if (!imageUrls.length) return;

  try {
    const { createClient } = await import("@/utils/supabase/client");
    const { STORAGE_BUCKET } = await import("@/lib/storage");
    const supabase = createClient();

    // Accepts BOTH shapes: a legacy absolute public URL and the bucket-relative
    // path that writers persist since the relative-storage-path migration. The
    // old marker-only extraction returned null for a bare path, so orphaned
    // uploads silently stopped being deleted.
    // @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L4
    const { extractStoragePath, isRelativeStoragePath, rawImageRefValue } =
      await import("@/lib/storage/url");
    // `remove()` takes bucket KEYS, not URLs — this is a persistence-side use,
    // so un-brand rather than resolve.
    const asKey = (raw: ImageRefInput): string =>
      typeof raw === "string" ? raw : rawImageRefValue(raw);
    const paths = imageUrls
      .map((raw) => extractStoragePath(raw) ?? (isRelativeStoragePath(raw) ? asKey(raw) : null))
      .filter((p): p is string => p !== null);

    if (paths.length > 0) {
      const { error } = await supabase.storage.from(STORAGE_BUCKET).remove(paths);
      if (error) console.error("[cleanupOrphanedUploads] remove error", error);
    }
  } catch (err) {
    console.error("[cleanupOrphanedUploads] best-effort cleanup failed", err);
  }
}

